import React, { useState, useContext } from "react";
import { AppContext } from '../../application/provider';
import { Modal, ModalHeader, ModalBody, Button, Form, FormGroup } from 'reactstrap';
import "./ModalCrearUsuario.css";
import UsuarioService from "../../axios_services/usuarios.service";
import { User, Briefcase, CreditCard, Paperclip, PackageCheck, RotateCcw, Search, CheckCircle } from "lucide-react";
import Loader from '../../components/Loader/Loader';

function ModalCrearUsuario({ isOpen }) {
    const [state, setState] = useContext(AppContext);
    const [loading, setLoading] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [allUsers, setAllUsers] = useState([]);

    React.useEffect(() => {
        if (isOpen) {
            UsuarioService.getUsuarios().then(data => {
                const activos = data.filter(u => String(u.estado) === '1');
                setAllUsers(activos);
            }).catch(err => console.error("Error cargando usuarios:", err));
        }
    }, [isOpen]);

    const jefePorPerfil = { 4: 3, 3: 6, 2: 6, 6: 1, 1: 9, 10: 9, 5: 10, 8: 10, 11: 10, 12: 10, 13: 10, 14: 10, 15: 10, 16: 10, 17: 10, 18: 10, 19: 6, 7: 20 };
    const MAX_FILE_SIZE = 5 * 1024 * 1024;

    // CATALOGOS
    const catalogosArray = state.catalogos?.tipos || [];
    const jornada_laboral = catalogosArray.filter(t => t.categoria_id == 5);
    const perfiles = state.catalogos?.perfiles || [];
    const perfilesSorted = [...perfiles].sort((a, b) => a.descripcion.localeCompare(b.descripcion));
    const provincia = catalogosArray.filter(t => t.categoria_id == 7);
    const centro_estudios = catalogosArray.filter(t => t.categoria_id == 23);
    const turnos_horario_laboral = catalogosArray.filter(t => t.categoria_id == 24);
    const entidades_financieras = catalogosArray.filter(t => t.categoria_id == 25);
    const isRestrictedArea = [5, 22].includes(Number(state.user?.perfil_id));
    const areas_laborales = [
        { tipo_id: 1, descripcion: 'NEGOCIOS' },
        { tipo_id: 2, descripcion: 'ADMINISTRACIÓN' },
        { tipo_id: 3, descripcion: 'CMAC' },
        { tipo_id: 4, descripcion: 'GERENCIA' },
        { tipo_id: 5, descripcion: 'TNP' }
    ].filter(a => {
        if (isRestrictedArea) return a.tipo_id === 1;
        return true;
    });

    const getPerfilesPorArea = (area_id) => {
        const id = Number(area_id);
        if (id === 1) return perfilesSorted.filter(p => [1, 2, 3, 4, 6, 23, 27, 29, 30].includes(Number(p.perfil_id)) || Number(p.area_id) === 1);
        if (id === 2) return perfilesSorted.filter(p => [5, 8, 10, 11, 12, 13, 15, 17, 18, 19, 21, 22, 24, 25, 26, 30].includes(Number(p.perfil_id)) || Number(p.area_id) === 2);
        if (id === 3) return perfilesSorted.filter(p => [7, 16, 20].includes(Number(p.perfil_id)) || Number(p.area_id) === 3);
        if (id === 4) return perfilesSorted.filter(p => [9, 14].includes(Number(p.perfil_id)) || Number(p.area_id) === 4);
        if (id === 5) return perfilesSorted.filter(p => [28].includes(Number(p.perfil_id)) || Number(p.area_id) === 5);
        return [];
    };
    const motivos_cese = catalogosArray.filter(t => t.categoria_id == 18);

    const createUsuario = async () => {
        try {
            if (state.usuario.nombre == null || state.usuario.nombre.length <= 1) {
                alert("Ingrese un nombre válido");
                return;
            }
            if (state.usuario.apellidos == null || state.usuario.apellidos.length <= 1) {
                alert("Ingrese un apellido válido");
                return;
            }
            if (state.usuario.perfil_id == null) {
                alert("Seleccione un perfil válido");
                return;
            }
            if (![9, 7, 20].includes(Number(state.usuario.perfil_id)) && !state.usuario.usuario_id_jefe_inmediato) {
                alert("Ingrese un jefe inmediato válido");
                return;
            }
            if (state.usuario.dni == null || state.usuario.dni.length !== 8) {
                alert("Ingrese un DNI válido");
                return;
            }
            if (state.usuario.fecha_ingreso == null) {
                alert("Ingrese la fecha de ingreso");
                return;
            }
            if (state.usuario.celular == null || state.usuario.celular.length !== 9) {
                alert("Ingrese un número de celular válido");
                return;
            }
            if (state.usuario.correo_personal && !/\S+@\S+\.\S+/.test(state.usuario.correo_personal)) {
                alert("Ingrese un correo electrónico válido");
                return;
            }

            setLoading(true);
            const dataToUpload = {
                ...state.usuario,
                usuario_creador_id: state.user?.usuario_id,
                periodo_fecha: state.periodo_fecha,
                perfil_id_jefe_inmediato: state.usuario.perfil_id_jefe_inmediato || null
            };

            const response = await UsuarioService.createUsuario(dataToUpload);
            alert("Usuario creado satisfactoriamente");
            setState(prev => ({ ...prev, isUpdated: true }));
            cerrarModal();
        } catch (error) {
            console.error("Error al crear el usuario:", error);
            const status = error?.response?.status;
            const msg = error?.response?.data?.message;
            if (status === 409) {
                alert(`⚠️ ${msg}\n\nPara reactivarlo, filtre los usuarios inactivos en el módulo de Usuarios y use el botón "Reactivar".`);
            } else if (status === 422 && msg) {
                alert(`⚠️ ${msg}`);
            } else {
                alert("Ocurrió un error al crear el usuario. Por favor, intente nuevamente.");
            }
        } finally {
            setLoading(false);
        }
    };

    const consultarDniApi = async () => {
        const dni = state.usuario?.dni;
        if (!dni || dni.length !== 8) {
            alert("Por favor, ingrese un DNI válido de 8 dígitos.");
            return;
        }

        try {
            const options = {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer 8ca65f8c0d27e05813b1c664c970edd1bd3ec0315d272c61fd44dad05986',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ dni })
            };

            const response = await fetch('https://api.json.pe/api/dni', options);
            const res = await response.json();

            if (res.success && res.data) {
                setState(prevState => ({
                    ...prevState,
                    usuario: {
                        ...prevState.usuario,
                        nombre: res.data.nombres || '',
                        apellidos: `${res.data.apellido_paterno || ''} ${res.data.apellido_materno || ''}`.trim()
                    }
                }));
            } else {
                alert("No se encontró información para el DNI ingresado.");
            }
        } catch (error) {
            console.error("Error al consultar el DNI:", error);
            alert("Ocurrió un error al consultar el API de DNI.");
        }
    };

    const updateUserRRHH = async () => {
        try {
            if (state.usuario.nombre == null || state.usuario.nombre.length <= 1) {
                alert("Ingrese un nombre válido");
                return;
            }
            if (state.usuario.apellidos == null || state.usuario.apellidos.length <= 1) {
                alert("Ingrese un apellido válido");
                return;
            }
            if (state.usuario.perfil_id == null) {
                alert("Seleccione un perfil válido");
                return;
            }
            if (![9, 7, 20].includes(Number(state.usuario.perfil_id)) && !state.usuario.usuario_id_jefe_inmediato) {
                alert("Ingrese un jefe inmediato válido");
                return;
            }
            if (state.usuario.dni == null || state.usuario.dni.length !== 8) {
                alert("Ingrese un DNI válido");
                return;
            }
            if (state.usuario.fecha_ingreso == null) {
                alert("Ingrese la fecha de ingreso");
                return;
            }
            if (state.usuario.celular == null || state.usuario.celular.length !== 9) {
                alert("Ingrese un número de celular válido");
                return;
            }
            if (state.usuario.correo_personal && !/\S+@\S+\.\S+/.test(state.usuario.correo_personal)) {
                alert("Ingrese un correo electrónico válido");
                return;
            }

            setLoading(true);
            const dataToSend = {
                ...state.usuario,
                usuario_actualizador_id: state.user?.usuario_id,
                periodo_fecha: state.periodo_fecha,
                perfil_id_jefe_inmediato: state.usuario.perfil_id_jefe_inmediato || null,
                tiene_casaca: Number(state.usuario.tiene_casaca) ? 1 : 0,
                tiene_fotocheck: Number(state.usuario.tiene_fotocheck) ? 1 : 0,
                tiene_equifax: Number(state.usuario.tiene_equifax) ? 1 : 0,
                devolvio_fotocheck: Number(state.usuario.devolvio_fotocheck) ? 1 : 0,
                resetear_password: state.usuario.resetear_password || false
            };

            await UsuarioService.updateUserRRHH(dataToSend);
            alert("Usuario actualizado satisfactoriamente");
            setState(prev => ({ ...prev, isUpdated: true }));
            cerrarModal();
        } catch (error) {
            console.error("Error al actualizar el usuario:", error);
            alert("Ocurrió un error al actualizar el usuario. Por favor, intente nuevamente.");
        } finally {
            setLoading(false);
        }
    };

    function cerrarModal() {
        setState((prevState) => ({
            ...prevState,
            modalCrearUsuario: false,
            usuario: null
        }));
    };

    return (
        state.modalCrearUsuario === true && (
            <Modal isOpen={state.modalCrearUsuario} className="custom-modal" backdrop={true}>
                <ModalHeader className="modal-header">
                    <div className="titulo">
                        <h1>{state.usuario && state.usuario.usuario_id ? "EDITAR USUARIO" : "REGISTRAR USUARIO"}</h1>
                        <Button className="close-btn" onClick={cerrarModal}>X</Button>
                    </div>
                </ModalHeader>
                <div className="modal-body-footer">
                    {loading ? (
                        <Loader />
                    ) : (
                        <ModalBody className="modal-body">
                            <Form className="fila">
                                <fieldset className="fieldset-container">
                                    <legend className="fieldset-legend">
                                        <User size={18} style={{ marginRight: '0.5rem' }} />
                                        Datos personales
                                    </legend>
                                    <FormGroup className="form-group">
                                        <label>DNI (*):</label>
                                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flex: 1 }}>
                                            <input
                                                className="form-control small-input"
                                                name="dni"
                                                type="text"
                                                placeholder="Ingrese el número de DNI"
                                                maxLength={8}
                                                value={state.usuario?.dni || ''}
                                                onChange={(e) => {
                                                    const val = e.target.value.replace(/\D/g, '');
                                                    if (val.length <= 8) {
                                                        setState((prevState) => ({
                                                            ...prevState,
                                                            usuario: { ...prevState.usuario, dni: val },
                                                        }));
                                                    }
                                                }}
                                            />
                                            <Button
                                                size="sm"
                                                color="info"
                                                onClick={consultarDniApi}
                                                title="Consultar DNI en RENIEC"
                                                style={{ padding: '0.2rem 0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                            >
                                                <Search size={16} />
                                            </Button>
                                        </div>
                                    </FormGroup>
                                    <FormGroup className="form-group">
                                        <label>Apellidos (*):</label>
                                        <input
                                            className="form-control small-input"
                                            name="apellidos"
                                            type="text"
                                            placeholder="Ingrese los apellidos"
                                            value={state.usuario?.apellidos || ''}
                                            onChange={(e) =>
                                                setState((prevState) => ({
                                                    ...prevState,
                                                    usuario: { ...prevState.usuario, apellidos: e.target.value },
                                                }))
                                            }
                                        />
                                    </FormGroup>
                                    <FormGroup className="form-group">
                                        <label>Nombres (*):</label>
                                        <input
                                            className="form-control small-input"
                                            name="nombre"
                                            type="text"
                                            placeholder="Ingrese los nombres"
                                            value={state.usuario?.nombre || ''}
                                            onChange={(e) =>
                                                setState((prevState) => ({
                                                    ...prevState,
                                                    usuario: { ...prevState.usuario, nombre: e.target.value },
                                                }))
                                            }
                                        />
                                    </FormGroup>
                                    <FormGroup className="form-group">
                                        <label>Fecha de nacimiento:</label>
                                        <input
                                            className="form-control small-input"
                                            name="fecha_nacimiento"
                                            type="date"
                                            value={state.usuario?.fecha_nacimiento || ''}
                                            onChange={(e) =>
                                                setState((prevState) => ({
                                                    ...prevState,
                                                    usuario: { ...prevState.usuario, fecha_nacimiento: e.target.value },
                                                }))
                                            }
                                        />
                                    </FormGroup>
                                    <FormGroup className="form-group">
                                        <label>Celular (*):</label>
                                        <input
                                            className="form-control small-input"
                                            name="celular"
                                            type="text"
                                            placeholder="Ingrese el número de celular"
                                            value={state.usuario?.celular || ''}
                                            onChange={(e) =>
                                                setState((prevState) => ({
                                                    ...prevState,
                                                    usuario: { ...prevState.usuario, celular: e.target.value },
                                                }))
                                            }
                                        />
                                    </FormGroup>
                                    <FormGroup className="form-group">
                                        <label>Correo electrónico:</label>
                                        <input
                                            className="form-control small-input"
                                            name="correo_personal"
                                            type="text"
                                            placeholder="Ingrese el correo electrónico"
                                            value={state.usuario?.correo_personal || ''}
                                            onChange={(e) =>
                                                setState((prevState) => ({
                                                    ...prevState,
                                                    usuario: { ...prevState.usuario, correo_personal: e.target.value },
                                                }))
                                            }
                                        />
                                    </FormGroup>
                                </fieldset>

                                <fieldset className="fieldset-container">
                                    <legend className="fieldset-legend">
                                        <Briefcase size={18} style={{ marginRight: '0.5rem' }} />
                                        Información laboral
                                    </legend>
                                    <FormGroup className="form-group">
                                        <label>Área (*):</label>
                                        <select
                                            className="form-control small-input"
                                            name="area_id"
                                            value={state.usuario?.area_id || ''}
                                            onChange={(e) =>
                                                setState((prevState) => ({
                                                    ...prevState,
                                                    usuario: { ...prevState.usuario, area_id: e.target.value },
                                                }))
                                            }
                                        >
                                            <option value="">Seleccione el área laboral</option>
                                            {areas_laborales.map((item) => (
                                                <option key={item.tipo_id} value={item.tipo_id}>{item.descripcion}</option>
                                            ))}
                                        </select>
                                    </FormGroup>
                                    <FormGroup className="form-group">
                                        <label>Cargo (Perfil) (*):</label>
                                        <select
                                            className="form-control small-input"
                                            name="perfil_id"
                                            value={state.usuario?.perfil_id || ''}
                                            disabled={!state.usuario?.area_id}
                                            onChange={(e) =>
                                                setState((prevState) => ({
                                                    ...prevState,
                                                    usuario: { ...prevState.usuario, perfil_id: e.target.value },
                                                }))
                                            }
                                        >
                                            <option value="">Seleccione el cargo</option>
                                            {getPerfilesPorArea(state.usuario?.area_id).map((item) => (
                                                <option key={item.perfil_id} value={item.perfil_id}>{item.descripcion}</option>
                                            ))}
                                        </select>
                                    </FormGroup>
                                    <FormGroup className="form-group">
                                        <label>Cargo del Jefe Inmediato:</label>
                                        <select
                                            className="form-control small-input"
                                            name="perfil_id_jefe_inmediato"
                                            value={state.usuario?.perfil_id_jefe_inmediato || ''}
                                            onChange={(e) =>
                                                setState((prevState) => ({
                                                    ...prevState,
                                                    usuario: { 
                                                        ...prevState.usuario, 
                                                        perfil_id_jefe_inmediato: e.target.value,
                                                        usuario_id_jefe_inmediato: '' 
                                                    },
                                                }))
                                            }
                                        >
                                            <option value="">Seleccione cargo</option>
                                            {perfilesSorted.map((item) => (
                                                <option key={item.perfil_id} value={item.perfil_id}>{item.descripcion}</option>
                                            ))}
                                        </select>
                                    </FormGroup>
                                    <FormGroup className="form-group">
                                        <label>Jefe inmediato (*):</label>
                                        <select
                                            className="form-control small-input"
                                            name="usuario_id_jefe_inmediato"
                                            value={state.usuario?.usuario_id_jefe_inmediato || ''}
                                            onChange={(e) =>
                                                setState((prevState) => ({
                                                    ...prevState,
                                                    usuario: { ...prevState.usuario, usuario_id_jefe_inmediato: e.target.value },
                                                }))
                                            }
                                            disabled={!state.usuario?.perfil_id_jefe_inmediato}
                                        >
                                            <option value="">Seleccione el jefe</option>
                                            {allUsers
                                                .filter(u => Number(u.perfil_id) === Number(state.usuario?.perfil_id_jefe_inmediato))
                                                .sort((a, b) => `${a.apellidos}, ${a.nombre}`.localeCompare(`${b.apellidos}, ${b.nombre}`))
                                                .map((item) => (
                                                    <option key={item.usuario_id} value={item.usuario_id}>
                                                        {item.apellidos}, {item.nombre}
                                                    </option>
                                                ))
                                            }
                                        </select>
                                    </FormGroup>
                                    <FormGroup className="form-group">
                                        <label>Correo corporativo:</label>
                                        <input
                                            className="form-control small-input"
                                            name="correo_corporativo"
                                            type="text"
                                            placeholder="Ingrese el correo corporativo"
                                            value={state.usuario?.correo_corporativo || ''}
                                            onChange={(e) =>
                                                setState((prevState) => ({
                                                    ...prevState,
                                                    usuario: { ...prevState.usuario, correo_corporativo: e.target.value },
                                                }))
                                            }
                                        />
                                    </FormGroup>
                                    <FormGroup className="form-group">
                                        <label>N° de contrato:</label>
                                        <input
                                            className="form-control small-input"
                                            name="n_contrato"
                                            type="text"
                                            placeholder="Ingrese el número de contrato"
                                            value={state.usuario?.n_contrato || ''}
                                            onChange={(e) =>
                                                setState((prevState) => ({
                                                    ...prevState,
                                                    usuario: { ...prevState.usuario, n_contrato: e.target.value },
                                                }))
                                            }
                                        />
                                    </FormGroup>
                                    <FormGroup className="form-group">
                                        <label>Fecha de ingreso:</label>
                                        <input
                                            className="form-control small-input"
                                            name="fecha_ingreso"
                                            type="date"
                                            value={state.usuario?.fecha_ingreso || ''}
                                            onChange={(e) =>
                                                setState((prevState) => ({
                                                    ...prevState,
                                                    usuario: { ...prevState.usuario, fecha_ingreso: e.target.value },
                                                }))
                                            }
                                        />
                                    </FormGroup>
                                    <FormGroup className="form-group">
                                        <label>Vencimiento contrato:</label>
                                        <input
                                            className="form-control small-input"
                                            name="fecha_termino_contrato"
                                            type="date"
                                            value={state.usuario?.fecha_termino_contrato || ''}
                                            onChange={(e) =>
                                                setState((prevState) => ({
                                                    ...prevState,
                                                    usuario: { ...prevState.usuario, fecha_termino_contrato: e.target.value },
                                                }))
                                            }
                                        />
                                    </FormGroup>
                                    <FormGroup className="form-group">
                                        <label>Sede laboral:</label>
                                        <select
                                            className="form-control small-input"
                                            name="provincia_id"
                                            value={state.usuario?.provincia_id || ''}
                                            onChange={(e) =>
                                                setState((prevState) => ({
                                                    ...prevState,
                                                    usuario: { ...prevState.usuario, provincia_id: e.target.value },
                                                }))
                                            }
                                        >
                                            <option value="">Seleccione la sede</option>
                                            {provincia.map((item) => (
                                                <option key={item.tipo_id} value={item.tipo_id}>{item.descripcion}</option>
                                            ))}
                                        </select>
                                    </FormGroup>
                                    <FormGroup className="form-group">
                                        <label>Tipo Jornada:</label>
                                        <select
                                            className="form-control small-input"
                                            name="tipo_jornada_laboral_id"
                                            value={state.usuario?.tipo_jornada_laboral_id || ''}
                                            onChange={(e) =>
                                                setState((prevState) => ({
                                                    ...prevState,
                                                    usuario: { ...prevState.usuario, tipo_jornada_laboral_id: e.target.value },
                                                }))
                                            }
                                        >
                                            <option value="">Seleccione jornada</option>
                                            {jornada_laboral.map((item) => (
                                                <option key={item.tipo_id} value={item.tipo_id}>{item.descripcion}</option>
                                            ))}
                                        </select>
                                    </FormGroup>
                                    <FormGroup className="form-group">
                                        <label>Turno:</label>
                                        <select
                                            className="form-control small-input"
                                            name="turno_laboral_id"
                                            value={state.usuario?.turno_laboral_id || ''}
                                            onChange={(e) =>
                                                setState((prevState) => ({
                                                    ...prevState,
                                                    usuario: { ...prevState.usuario, turno_laboral_id: e.target.value },
                                                }))
                                            }
                                        >
                                            <option value="">Seleccione turno</option>
                                            {turnos_horario_laboral.map((item) => (
                                                <option key={item.tipo_id} value={item.tipo_id}>{item.descripcion}</option>
                                            ))}
                                        </select>
                                    </FormGroup>
                                    <FormGroup className="form-group">
                                        <label>Centro Estudios:</label>
                                        <select
                                            className="form-control small-input"
                                            name="centro_estudios_id"
                                            value={state.usuario?.centro_estudios_id || ''}
                                            onChange={(e) =>
                                                setState((prevState) => ({
                                                    ...prevState,
                                                    usuario: { ...prevState.usuario, centro_estudios_id: e.target.value },
                                                }))
                                            }
                                        >
                                            <option value="">Seleccione centro</option>
                                            {centro_estudios.map((item) => (
                                                <option key={item.tipo_id} value={item.tipo_id}>{item.descripcion}</option>
                                            ))}
                                        </select>
                                    </FormGroup>
                                </fieldset>

                                <fieldset className="fieldset-container">
                                    <legend className="fieldset-legend">
                                        <CreditCard size={18} style={{ marginRight: '0.5rem' }} />
                                        Información bancaria
                                    </legend>
                                    <FormGroup className="form-group">
                                        <label>Entidad Financiera:</label>
                                        <select
                                            className="form-control small-input"
                                            name="entidad_financiera_id"
                                            value={state.usuario?.entidad_financiera_id || ''}
                                            onChange={(e) =>
                                                setState((prevState) => ({
                                                    ...prevState,
                                                    usuario: { ...prevState.usuario, entidad_financiera_id: e.target.value },
                                                }))
                                            }
                                        >
                                            <option value="">Seleccione entidad</option>
                                            {entidades_financieras.map((item) => (
                                                <option key={item.tipo_id} value={item.tipo_id}>{item.descripcion}</option>
                                            ))}
                                        </select>
                                    </FormGroup>
                                    <FormGroup className="form-group">
                                        <label>N° Cuenta:</label>
                                        <input
                                            className="form-control small-input"
                                            name="n_cuenta_bancaria"
                                            type="text"
                                            disabled={!state.usuario?.entidad_financiera_id}
                                            placeholder="Ingrese número de cuenta"
                                            value={state.usuario?.n_cuenta_bancaria || ''}
                                            onChange={(e) =>
                                                setState((prevState) => ({
                                                    ...prevState,
                                                    usuario: { ...prevState.usuario, n_cuenta_bancaria: e.target.value },
                                                }))
                                            }
                                        />
                                    </FormGroup>
                                    <FormGroup className="form-group">
                                        <label>CCI:</label>
                                        <input
                                            className="form-control small-input"
                                            name="n_cuenta_interbancaria"
                                            type="text"
                                            disabled={!state.usuario?.entidad_financiera_id}
                                            placeholder="Ingrese CCI"
                                            value={state.usuario?.n_cuenta_interbancaria || ''}
                                            onChange={(e) =>
                                                setState((prevState) => ({
                                                    ...prevState,
                                                    usuario: { ...prevState.usuario, n_cuenta_interbancaria: e.target.value },
                                                }))
                                            }
                                        />
                                    </FormGroup>
                                </fieldset>

                                <fieldset className="fieldset-container">
                                    <legend className="fieldset-legend">
                                        <Paperclip size={18} style={{ marginRight: '0.5rem' }} />
                                        Documentos
                                    </legend>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.75rem' }}>
                                        <FormGroup className="form-group">
                                            <label>DNI: {state.usuario?.archivo_dni && <CheckCircle size={14} color="green" />}</label>
                                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <input type="file" id="file-dni" style={{ display: 'none' }} onChange={(e) => setState(prev => ({ ...prev, usuario: { ...prev.usuario, archivo_dni: e.target.files[0] } }))} />
                                                <button type="button" style={{ backgroundColor: '#e0e0e0', border: '1px solid #ccc', borderRadius: '4px', padding: '2px 8px', fontSize: '0.7rem', cursor: 'pointer', color: '#333' }} onClick={() => document.getElementById('file-dni').click()}>
                                                    Elegir archivo
                                                </button>
                                                <span style={{ fontSize: '0.7rem', color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {state.usuario?.archivo_dni ? state.usuario.archivo_dni.name : "Ningún archivo seleccionado"}
                                                </span>
                                            </div>
                                        </FormGroup>
                                        <FormGroup className="form-group">
                                            <label>Ficha: {state.usuario?.archivo_ficha && <CheckCircle size={14} color="green" />}</label>
                                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <input type="file" id="file-ficha" style={{ display: 'none' }} onChange={(e) => setState(prev => ({ ...prev, usuario: { ...prev.usuario, archivo_ficha: e.target.files[0] } }))} />
                                                <button type="button" style={{ backgroundColor: '#e0e0e0', border: '1px solid #ccc', borderRadius: '4px', padding: '2px 8px', fontSize: '0.7rem', cursor: 'pointer', color: '#333' }} onClick={() => document.getElementById('file-ficha').click()}>
                                                    Elegir archivo
                                                </button>
                                                <span style={{ fontSize: '0.7rem', color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {state.usuario?.archivo_ficha ? state.usuario.archivo_ficha.name : "Ningún archivo seleccionado"}
                                                </span>
                                            </div>
                                        </FormGroup>
                                        <FormGroup className="form-group">
                                            <label>Contrato: {state.usuario?.archivo_contrato && <CheckCircle size={14} color="green" />}</label>
                                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <input type="file" id="file-contrato" style={{ display: 'none' }} onChange={(e) => setState(prev => ({ ...prev, usuario: { ...prev.usuario, archivo_contrato: e.target.files[0] } }))} />
                                                <button type="button" style={{ backgroundColor: '#e0e0e0', border: '1px solid #ccc', borderRadius: '4px', padding: '2px 8px', fontSize: '0.7rem', cursor: 'pointer', color: '#333' }} onClick={() => document.getElementById('file-contrato').click()}>
                                                    Elegir archivo
                                                </button>
                                                <span style={{ fontSize: '0.7rem', color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {state.usuario?.archivo_contrato ? state.usuario.archivo_contrato.name : "Ningún archivo seleccionado"}
                                                </span>
                                            </div>
                                        </FormGroup>
                                        <FormGroup className="form-group">
                                            <label>Recibo: {state.usuario?.archivo_recibo_luz_agua && <CheckCircle size={14} color="green" />}</label>
                                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <input type="file" id="file-recibo" style={{ display: 'none' }} onChange={(e) => setState(prev => ({ ...prev, usuario: { ...prev.usuario, archivo_recibo_luz_agua: e.target.files[0] } }))} />
                                                <button type="button" style={{ backgroundColor: '#e0e0e0', border: '1px solid #ccc', borderRadius: '4px', padding: '2px 8px', fontSize: '0.7rem', cursor: 'pointer', color: '#333' }} onClick={() => document.getElementById('file-recibo').click()}>
                                                    Elegir archivo
                                                </button>
                                                <span style={{ fontSize: '0.7rem', color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {state.usuario?.archivo_recibo_luz_agua ? state.usuario.archivo_recibo_luz_agua.name : "Ningún archivo seleccionado"}
                                                </span>
                                            </div>
                                        </FormGroup>
                                        <FormGroup className="form-group">
                                            <label>Certijoven: {state.usuario?.archivo_certijoven && <CheckCircle size={14} color="green" />}</label>
                                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <input type="file" id="file-certijoven" style={{ display: 'none' }} onChange={(e) => setState(prev => ({ ...prev, usuario: { ...prev.usuario, archivo_certijoven: e.target.files[0] } }))} />
                                                <button type="button" style={{ backgroundColor: '#e0e0e0', border: '1px solid #ccc', borderRadius: '4px', padding: '2px 8px', fontSize: '0.7rem', cursor: 'pointer', color: '#333' }} onClick={() => document.getElementById('file-certijoven').click()}>
                                                    Elegir archivo
                                                </button>
                                                <span style={{ fontSize: '0.7rem', color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {state.usuario?.archivo_certijoven ? state.usuario.archivo_certijoven.name : "Ningún archivo seleccionado"}
                                                </span>
                                            </div>
                                        </FormGroup>
                                    </div>
                                </fieldset>

                                <fieldset className="fieldset-container">
                                    <legend className="fieldset-legend">
                                        <PackageCheck size={18} style={{ marginRight: '0.5rem' }} />
                                        Instrumentos
                                    </legend>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                        <FormGroup className="form-group" style={{ minWidth: 'auto' }}>
                                            <label>Casaca:</label>
                                            <input type="checkbox" checked={Boolean(Number(state.usuario?.tiene_casaca))} onChange={(e) => setState(prev => ({ ...prev, usuario: { ...prev.usuario, tiene_casaca: e.target.checked ? 1 : 0 } }))} />
                                        </FormGroup>
                                        <FormGroup className="form-group" style={{ minWidth: 'auto' }}>
                                            <label>Fotocheck:</label>
                                            <input type="checkbox" checked={Boolean(Number(state.usuario?.tiene_fotocheck))} onChange={(e) => setState(prev => ({ ...prev, usuario: { ...prev.usuario, tiene_fotocheck: e.target.checked ? 1 : 0 } }))} />
                                        </FormGroup>
                                        <FormGroup className="form-group" style={{ minWidth: 'auto' }}>
                                            <label>Equifax:</label>
                                            <input type="checkbox" checked={Boolean(Number(state.usuario?.tiene_equifax))} onChange={(e) => setState(prev => ({ ...prev, usuario: { ...prev.usuario, tiene_equifax: e.target.checked ? 1 : 0 } }))} />
                                        </FormGroup>
                                    </div>
                                </fieldset>

                                {state.usuario?.usuario_id && (
                                    <fieldset className="fieldset-container">
                                        <legend className="fieldset-legend">
                                            <RotateCcw size={18} style={{ marginRight: '0.5rem' }} />
                                            Seguridad
                                        </legend>
                                        <FormGroup className="form-group">
                                            <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>Restablecer contraseña?</label>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 'normal' }}>
                                                    <input type="radio" name="reset_pwd" checked={!state.usuario.resetear_password} onChange={() => setState(prev => ({ ...prev, usuario: { ...prev.usuario, resetear_password: false } }))} /> Mantener actual
                                                </label>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 'normal' }}>
                                                    <input type="radio" name="reset_pwd" checked={state.usuario.resetear_password === true} onChange={() => setState(prev => ({ ...prev, usuario: { ...prev.usuario, resetear_password: true } }))} /> Restablecer a (MEC2026!Pass)
                                                </label>
                                            </div>
                                        </FormGroup>
                                    </fieldset>
                                )}
                            </Form>
                            <div className="button-container">
                                <Button id="boton-form-modal" className="button-custom" disabled={isSaved} onClick={async () => {
                                    if (!window.confirm("¿Está seguro?")) return;
                                    setIsSaved(true);
                                    try {
                                        if (state.usuario?.usuario_id) await updateUserRRHH();
                                        else await createUsuario();
                                    } catch (err) { alert("Error al procesar"); }
                                    finally { setIsSaved(false); }
                                }}>
                                    {state.usuario?.usuario_id ? "ACTUALIZAR" : "REGISTRAR"}
                                </Button>
                            </div>
                        </ModalBody>
                    )}
                </div>
            </Modal>
        )
    );
}

export default ModalCrearUsuario;